import { EditablePresentationSourceData } from "./../../PresentationSectionProps";
import * as yup from "yup";
import { createOptionalNumericValidationWithMax } from "@cma/shared/helpers/yup-extensions";
import { stringToFloat } from "@cma/shared/helpers/form-converters";
import { KeyFeaturesSectionView } from "@cma/presentation-generation-logic";

export type KeyFeatureFieldType =
  | "text"
  | "number"
  | "currency"
  | "sqft"
  | "boolean";

export type EditableKeyFeatures = keyof Pick<
  KeyFeaturesSectionView,
  | "propertyType"
  | "yearBuilt"
  | "neighborhood"
  | "livingSpace"
  | "beds"
  | "baths"
  | "lotSize"
  | "garageParkingSpaces"
  | "hoaFees"
  | "listPrice"
  | "soldPrice"
  | "hasDoormanAttendance"
  | "roomsTotal"
>;

export const getFieldType = (
  valueName: EditableKeyFeatures
): KeyFeatureFieldType => {
  const fieldTypeMap: Record<EditableKeyFeatures, KeyFeatureFieldType> = {
    // Text fields
    propertyType: "text",
    neighborhood: "text",

    // Number fields
    yearBuilt: "number",
    beds: "number",
    baths: "number",
    roomsTotal: "number",
    garageParkingSpaces: "number",

    // Currency fields
    listPrice: "currency",
    soldPrice: "currency",
    hoaFees: "currency",

    // Square footage fields
    livingSpace: "sqft",
    lotSize: "sqft",

    // Boolean fields
    hasDoormanAttendance: "boolean",
  };

  return fieldTypeMap[valueName];
};

const NUMERIC_FIELD_MAX_VALUES = {
  yearBuilt: 9999,
  beds: 99,
  baths: 99,
  roomsTotal: 100,
  garageParkingSpaces: 99,
  listPrice: 999_999_999,
  soldPrice: 999_999_999,
  livingSpace: 999_999,
  lotSize: 999_999,
  hoaFees: 999_999,
};

const getFieldMaxValue = (fieldName: EditableKeyFeatures): number => {
  return (
    NUMERIC_FIELD_MAX_VALUES[
      fieldName as keyof typeof NUMERIC_FIELD_MAX_VALUES
    ] || 999_999_999
  );
};

export const isNumericFieldValueAllowed = (
  fieldName: EditableKeyFeatures,
  value: number | undefined = 0
) => {
  return value >= 0 && value <= getFieldMaxValue(fieldName);
};

export const keyFeaturesValidationSchema = yup.object().shape({
  neighborhood: yup.string().optional(),
  propertyType: yup.string().optional(),
  beds: createOptionalNumericValidationWithMax(
    "Bedrooms",
    NUMERIC_FIELD_MAX_VALUES.beds
  ),
  baths: createOptionalNumericValidationWithMax(
    "Bathrooms",
    NUMERIC_FIELD_MAX_VALUES.baths
  ),
  yearBuilt: createOptionalNumericValidationWithMax(
    "Year Built",
    NUMERIC_FIELD_MAX_VALUES.yearBuilt
  ),
  garageParkingSpaces: createOptionalNumericValidationWithMax(
    "Parking Spaces",
    NUMERIC_FIELD_MAX_VALUES.garageParkingSpaces
  ),
  roomsTotal: createOptionalNumericValidationWithMax(
    "Total Rooms",
    NUMERIC_FIELD_MAX_VALUES.roomsTotal
  ),
  listPrice: createOptionalNumericValidationWithMax(
    "List Price",
    NUMERIC_FIELD_MAX_VALUES.listPrice
  ),
  soldPrice: createOptionalNumericValidationWithMax(
    "Sold Price",
    NUMERIC_FIELD_MAX_VALUES.soldPrice
  ),
  livingSpace: createOptionalNumericValidationWithMax(
    "Living Space",
    NUMERIC_FIELD_MAX_VALUES.livingSpace
  ),
  lotSize: createOptionalNumericValidationWithMax(
    "Lot Size",
    NUMERIC_FIELD_MAX_VALUES.lotSize
  ),
  hasDoormanAttendance: yup
    .string()
    .optional()
    .test(
      "is-valid-doorman-value",
      "Please select Yes or No",
      (value) => value === undefined || value === "yes" || value === "no"
    ),
  hoaFees: createOptionalNumericValidationWithMax(
    "Monthly Fees",
    NUMERIC_FIELD_MAX_VALUES.hoaFees
  ),
});

type KeyFeaturesValidationSchema =
  typeof keyFeaturesValidationSchema.__outputType;

export const getKeyFeatureValidator = (valueName: string) => {
  const schema = keyFeaturesValidationSchema.fields;
  return schema[valueName as keyof typeof schema] || yup.string().optional();
};

export const validateKeyFeatureField = async (
  valueName: string,
  value: any
): Promise<string | null> => {
  try {
    await keyFeaturesValidationSchema.validateAt(valueName, {
      [valueName]: value,
    });
    return null;
  } catch (error) {
    if (error instanceof yup.ValidationError) {
      return error.message;
    }
    return "Invalid value";
  }
};

export const convertKeyFeaturesValidationSchemaToFeaturedProperty = (
  formData: KeyFeaturesValidationSchema
): NonNullable<EditablePresentationSourceData["featuredProperty"]> => {
  const featuredProperty: NonNullable<
    EditablePresentationSourceData["featuredProperty"]
  > = {
    beds:
      formData.beds === undefined
        ? undefined
        : formData.beds === null || formData.beds === ""
          ? null
          : stringToFloat(formData.beds),
    baths:
      formData.baths === undefined
        ? undefined
        : formData.baths === null || formData.baths === ""
          ? null
          : stringToFloat(formData.baths),
    yearBuilt:
      formData.yearBuilt === undefined
        ? undefined
        : formData.yearBuilt === null || formData.yearBuilt === ""
          ? null
          : stringToFloat(formData.yearBuilt),
    garageParkingSpaces:
      formData.garageParkingSpaces === undefined
        ? undefined
        : formData.garageParkingSpaces === null ||
            formData.garageParkingSpaces === ""
          ? null
          : stringToFloat(formData.garageParkingSpaces),
    roomsTotal:
      formData.roomsTotal === undefined
        ? undefined
        : formData.roomsTotal === null || formData.roomsTotal === ""
          ? null
          : stringToFloat(formData.roomsTotal),
    currentPrice:
      formData.listPrice === undefined
        ? undefined
        : formData.listPrice === null || formData.listPrice === ""
          ? null
          : stringToFloat(formData.listPrice),
    soldPrice:
      formData.soldPrice === undefined
        ? undefined
        : formData.soldPrice === null || formData.soldPrice === ""
          ? null
          : stringToFloat(formData.soldPrice),
    hoaFees:
      formData.hoaFees === undefined
        ? undefined
        : formData.hoaFees === null || formData.hoaFees === ""
          ? null
          : stringToFloat(formData.hoaFees),
    livingSpace:
      formData.livingSpace === undefined
        ? undefined
        : formData.livingSpace === null || formData.livingSpace === ""
          ? null
          : stringToFloat(formData.livingSpace),
    lotSize:
      formData.lotSize === undefined
        ? undefined
        : formData.lotSize === null || formData.lotSize === ""
          ? null
          : stringToFloat(formData.lotSize),
    hasDoormanAttendance:
      formData.hasDoormanAttendance === undefined
        ? undefined
        : formData.hasDoormanAttendance === null ||
            formData.hasDoormanAttendance === ""
          ? null
          : formData.hasDoormanAttendance === "yes",
    propertyType:
      formData.propertyType === undefined
        ? undefined
        : formData.propertyType === null || formData.propertyType === ""
          ? null
          : String(formData.propertyType),
    neighborhood:
      formData.neighborhood === undefined
        ? undefined
        : formData.neighborhood === null || formData.neighborhood === ""
          ? null
          : String(formData.neighborhood),
  };

  return featuredProperty;
};
